import { useEffect, useRef, useState } from 'react';

import { Box, Link } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import { financeConfigurationAtom } from '~atoms';

import { useStyles } from './style';
import { Table } from '../../../../components';
import { isObject, decimalBase, getFinanceTableHeaders } from '../../../../shared/utility';
import * as actions from '../../../../store/actions';

interface ActivePeriodProps {
	financeType?: string;
	date?: string;
	onFetchCurrentPersonalBillingPeriod?(...args: unknown[]): unknown;
	isSuperAdmin?: boolean;
	currentPersonalBillingPeriod?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
}

const ActivePeriod = (props: ActivePeriodProps) => {
	const { onFetchCurrentPersonalBillingPeriod, currentPersonalBillingPeriod } = props;
	const { t } = useTranslation();
	const financeConfiguration = useAtomValue(financeConfigurationAtom);

	const classes = useStyles();

	const shouldDoInitialFetch = useRef(true);

	const { data: currentPersonalBillingPeriodData, loading: currentPersonalBillingPeriodLoading } =
		currentPersonalBillingPeriod;

	useEffect(() => {
		if (shouldDoInitialFetch.current && !currentPersonalBillingPeriodLoading) {
			onFetchCurrentPersonalBillingPeriod(financeConfiguration.excludeOneOffPayments);
			shouldDoInitialFetch.current = false;
		}
	}, [currentPersonalBillingPeriodLoading]);

	useEffect(() => {
		if (!currentPersonalBillingPeriodLoading && !shouldDoInitialFetch.current) {
			onFetchCurrentPersonalBillingPeriod(financeConfiguration.excludeOneOffPayments);
		}
	}, [financeConfiguration.excludeOneOffPayments]);

	const tableHeader = getFinanceTableHeaders(false, false, true);

	const tableBody =
		isObject(currentPersonalBillingPeriodData) ?
			[
				[
					{
						content: (
							<Box className={classes.periodContent}>
								<Link
									color='inherit'
									component={RouterLink}
									to={`/financeManagement/personal/${currentPersonalBillingPeriodData.start}/${currentPersonalBillingPeriodData.title}/billing`}
								>
									{currentPersonalBillingPeriodData.title}
								</Link>
							</Box>
						),
					},
					{
						content: decimalBase(
							currentPersonalBillingPeriodData.amount.subtotal,
							currentPersonalBillingPeriodData.amount.currencySymbol
						),
					},
					{
						content: decimalBase(
							currentPersonalBillingPeriodData.amount.discount,
							currentPersonalBillingPeriodData.amount.currencySymbol
						),
					},
					{
						content: decimalBase(
							currentPersonalBillingPeriodData.amount.subtotal -
								currentPersonalBillingPeriodData.amount.discount,
							currentPersonalBillingPeriodData.amount.currencySymbol
						),
					},
					{
						content: decimalBase(
							currentPersonalBillingPeriodData.amount.priceVat,
							currentPersonalBillingPeriodData.amount.currencySymbol
						),
					},
					{
						content: decimalBase(
							currentPersonalBillingPeriodData.amount.refund,
							currentPersonalBillingPeriodData.amount.currencySymbol
						),
					},
					{
						content: decimalBase(
							currentPersonalBillingPeriodData.amount.totalPrice,
							currentPersonalBillingPeriodData.amount.currencySymbol
						),
					},
					{
						content: (
							<Box className={classes.open}>
								{decimalBase(
									currentPersonalBillingPeriodData.open,
									currentPersonalBillingPeriodData.amount.currencySymbol
								)}
							</Box>
						),
					},
				],
			]
		:	Array(2)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })));

	return (
		<div className={classes.root}>
			<Table
				body={tableBody}
				header={tableHeader}
				isNotPaginate={true}
				loading={currentPersonalBillingPeriodLoading}
				title={t('ui.label.active')}
				withoutPages={true}
			/>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		currentPersonalBillingPeriod: state.details.currentPersonalBillingPeriod,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchCurrentPersonalBillingPeriod: (excludeOneOffPayments: boolean) =>
			dispatch(actions.fetchCurrentPersonalBillingPeriod(excludeOneOffPayments)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivePeriod);
